import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Timer, Zap, Target, Trophy, Clock } from "lucide-react";
import type { Task, DailyStats } from "@/types";
import {
    isSameDay,
    formatHoursMinutes,
    getElapsedSeconds,
    getDateKey,
} from "@/utils/helpers";

interface OverviewProps {
    tasks: Task[];
    selectedDate: Date;
    dailyStats?: Record<string, DailyStats>;
}

export function Overview({
    tasks,
    selectedDate,
    dailyStats = {},
}: OverviewProps) {
    const isToday = isSameDay(selectedDate, new Date());
    const dateKey = getDateKey(selectedDate);
    const savedStats = dailyStats[dateKey];

    // For past days with saved stats, use the saved values
    // Otherwise calculate from current tasks
    // Exclude backlog tasks from daily progress stats
    const todaysTasks = tasks.filter(
        (t) => !t.isBacklog && isSameDay(new Date(t.createdAt), selectedDate)
    );

    let totalTasks: number;
    let completedCount: number;
    let totalWorkedSeconds: number;
    let remainingSeconds: number;
    let totalEstimatedSeconds: number;
    let activeTasks: Task[];

    if (!isToday && savedStats) {
        // Use saved stats for past days
        totalTasks = savedStats.totalTasks;
        completedCount = savedStats.completedTasks;
        totalWorkedSeconds = savedStats.totalWorkedSeconds;
        totalEstimatedSeconds = savedStats.totalEstimatedMinutes * 60;
        remainingSeconds = 0; // Past days have no remaining work
        activeTasks = []; // No active tasks in past days
    } else {
        // Calculate from current tasks
        totalTasks = todaysTasks.length;
        completedCount = todaysTasks.filter(
            (t) => t.status === "completed"
        ).length;
        totalWorkedSeconds = todaysTasks.reduce(
            (sum, t) => sum + getElapsedSeconds(t),
            0
        );
        totalEstimatedSeconds = todaysTasks.reduce(
            (sum, t) => sum + t.estimatedMinutes * 60,
            0
        );

        // Calculate remaining time only for incomplete tasks (pending + active)
        // For each incomplete task: remaining = estimated - elapsed
        remainingSeconds = todaysTasks
            .filter((t) => t.status !== "completed")
            .reduce((sum, t) => {
                const elapsed = getElapsedSeconds(t);
                const estimated = t.estimatedMinutes * 60;
                return sum + Math.max(0, estimated - elapsed);
            }, 0);

        activeTasks = todaysTasks.filter((t) => t.status === "active");
    }

    const totalWorkedMinutes = Math.floor(totalWorkedSeconds / 60);
    const remainingMinutes = Math.floor(remainingSeconds / 60);

    // Progress calculation:
    // - Completed tasks count as 100% of their estimated time
    // - Active/pending tasks count based on time worked vs estimated
    // This way, completing a task early still shows full progress for that task
    const completedEstimatedSeconds = todaysTasks
        .filter((t) => t.status === "completed")
        .reduce((sum, t) => sum + t.estimatedMinutes * 60, 0);

    const incompleteWorkedSeconds = todaysTasks
        .filter((t) => t.status !== "completed")
        .reduce((sum, t) => sum + getElapsedSeconds(t), 0);

    const progressSeconds = completedEstimatedSeconds + incompleteWorkedSeconds;

    const timeProgressPercentage =
        totalEstimatedSeconds > 0
            ? Math.round((progressSeconds / totalEstimatedSeconds) * 100)
            : totalTasks > 0 && completedCount === totalTasks
            ? 100
            : 0;

    // Calculate estimated finish time (current time + remaining seconds)
    const now = new Date();
    const estimatedFinishTime = new Date(
        now.getTime() + remainingSeconds * 1000
    );

    // Format finish time with day context
    const formatFinishTime = (finishDate: Date) => {
        const timeStr = finishDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });

        const todayDate = new Date();
        const tomorrow = new Date(todayDate);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const finishDay = finishDate.toDateString();
        const todayStr = todayDate.toDateString();
        const tomorrowStr = tomorrow.toDateString();

        if (finishDay === todayStr) {
            return timeStr;
        } else if (finishDay === tomorrowStr) {
            return `tomorrow at ${timeStr}`;
        } else {
            // Format as "Mon 5th at 21:00"
            const dayName = finishDate.toLocaleDateString([], {
                weekday: "short",
            });
            const dayNum = finishDate.getDate();
            const suffix = getDaySuffix(dayNum);
            return `${dayName} ${dayNum}${suffix} at ${timeStr}`;
        }
    };

    const getDaySuffix = (day: number) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
            case 1:
                return "st";
            case 2:
                return "nd";
            case 3:
                return "rd";
            default:
                return "th";
        }
    };

    return (
        <Card className="overflow-hidden border-0 shadow-lg">
            <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                            <Target className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="font-semibold">Daily Progress</h2>
                            <p className="text-xs text-white/70">
                                {activeTasks.length > 0
                                    ? "Task in progress..."
                                    : "Ready to focus"}
                            </p>
                        </div>
                    </div>
                    <Badge
                        variant="secondary"
                        className="bg-white/20 text-white border-0 backdrop-blur-sm"
                    >
                        {Math.min(100, timeProgressPercentage)}% Complete
                    </Badge>
                </div>

                <div className="mb-4">
                    <Progress
                        value={Math.min(100, timeProgressPercentage)}
                        className="h-2 bg-white/20"
                        indicatorClassName="bg-white"
                    />
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-3 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-300" />
                        </div>
                        <div className="text-lg sm:text-2xl font-bold">
                            {formatHoursMinutes(totalWorkedMinutes)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-white/70">
                            Worked
                        </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-3 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Timer className="w-3 h-3 sm:w-4 sm:h-4 text-orange-300" />
                        </div>
                        <div className="text-lg sm:text-2xl font-bold">
                            {formatHoursMinutes(remainingMinutes)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-white/70">
                            Remaining
                        </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-3 text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                            <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-green-300" />
                        </div>
                        <div className="text-lg sm:text-2xl font-bold">
                            {completedCount}
                            <span className="text-sm sm:text-lg text-white/50">
                                /{totalTasks}
                            </span>
                        </div>
                        <div className="text-[10px] sm:text-xs text-white/70">
                            Done
                        </div>
                    </div>
                </div>

                {/* Estimated finish time - only show if there's remaining work */}
                {isToday && remainingMinutes > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/20">
                        <div className="flex items-center justify-center gap-2 text-white/90">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                                Finish by{" "}
                                <span className="font-semibold">
                                    {formatFinishTime(estimatedFinishTime)}
                                </span>{" "}
                                if no breaks
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
}
